// Premium, print-ready invoice layout — ported verbatim from erp-thekseoul's
// canonical InvoiceDocument (src/components/invoices/InvoiceDocument.tsx),
// which every portal (rxk, pick-k, medi-k) is expected to reuse rather than
// re-derive. Only the display font changed (Fraunces isn't loaded here —
// Playfair Display is rxk's existing serif). Keep this file in sync with
// the ERP original when that one is updated.
import type { ReactNode } from 'react';
import { Landmark, Mail, Globe, MapPin } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { formatPaymentMethodLabel, paymentStatusLabel, paymentStatusColor } from '@/lib/paymentStatus';
import { invoiceText } from '@/lib/invoiceTextFields';
import { RefundWatermark, refundStatusLabel, type InvoiceRefund } from './RefundWatermark';

export interface InvoiceTemplateData {
  issuer_name: string | null;
  legal_entity_name: string | null;
  concern_line: string | null;
  issuer_business_number: string | null;
  trade_license_no: string | null;
  // The practical/office address, shown in the FROM block. Falls back to
  // issuer_address (the legal/registered address, printed in the footer)
  // when not set separately, so the FROM block never goes blank.
  office_address?: string | null;
  issuer_address: string | null;
  issuer_contact: string | null;
  issuer_email: string | null;
  issuer_website: string | null;
  logo_url: string | null;
  signature_url: string | null;
  stamp_url: string | null;
  watermark_url: string | null;
  // Where the "Scan to Connect" QR points — a brand's social page (e.g.
  // Facebook) if set, else falls back to the website/email below.
  social_url: string | null;
  // VAT is always price-inclusive here — this only controls whether the
  // actual currency amount is printed alongside the rate, or just the rate
  // as a plain "prices include X% VAT" note. Defaults true at the DB level.
  show_vat_amount?: boolean;
  text_overrides?: Record<string, string> | null;
}

export interface InvoiceLineItem {
  sku?: string | null;
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
  // Physical units (core_product_authenticity_codes.inventory_code) scanned
  // in and linked to this order for this line — populated at view time,
  // not stored on the invoice_requests row itself.
  serials?: string[];
}

export interface InvoiceRequestData {
  doc_type: string;
  doc_number: string | null;
  issue_date: string | null;
  due_date: string | null;
  currency: string;
  recipient_name: string | null;
  recipient_email: string | null;
  company_name: string | null;
  business_number: string | null;
  company_address: string | null;
  company_contact: string | null;
  line_items: InvoiceLineItem[];
  subtotal: number;
  discount_bdt: number;
  shipping_bdt: number;
  tax_rate: number;
  tax_amount: number;
  total_amount: number;
  notes: string | null;
  payment_terms: string | null;
}

const DOC_TYPE_TEXT_KEY: Record<string, string> = {
  invoice: 'doc_label_invoice',
  proforma_invoice: 'doc_label_proforma_invoice',
  purchase_order: 'doc_label_purchase_order',
  receipt: 'doc_label_receipt',
};

// Design tokens — single accent (brass/gold) against the app's existing
// brand navy, used sparingly (rule lines, seal label, QR frame) rather
// than as a competing second color.
const NAVY = '#1B2A4A';
const NAVY_DEEP = '#0E1A30';
const GOLD = '#AE8A4C';
const INK = '#26324A';
const MUTED = '#6B7280';
const FAINT = '#9CA3AF';
const HAIRLINE = '#E7E9EE';
const SOFT_BG = '#F6F7FB';

function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'BDT', minimumFractionDigits: 2 }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-[8.5px] font-bold uppercase tracking-[0.14em] mb-[3px]" style={{ color: FAINT }}>
        {label}
      </p>
      <div className="text-[12px] leading-[1.45]" style={{ color: INK }}>
        {children}
      </div>
    </div>
  );
}

export interface InvoicePayment {
  paid: boolean;
  method: string | null;
  // Pre-resolved live display name (e.g. from core_payment_gateways) for
  // an online gateway's payment_method key — falls back to
  // formatPaymentMethodLabel(method) below when the caller hasn't
  // resolved one (e.g. no gateway match, or the caller predates this).
  methodLabel?: string;
  amountBdt: number | null;
  paidAt: string | null;
}

export function InvoiceDocument({
  template,
  request,
  payment,
  salesPerson,
  soldBy,
  refund,
  cancelled,
  cancelReason,
}: {
  template: InvoiceTemplateData;
  request: InvoiceRequestData;
  payment?: InvoicePayment;
  salesPerson?: string | null;
  /** Mini-shop/group-buy reseller who drove this sale (core_orders.seller_ref)
   *  — distinct from salesPerson, which is the internal staff member who
   *  confirmed the order. Only set for pickk_seller/pickk_gb channel orders. */
  soldBy?: string | null;
  /** Set when the linked order has an approved refund (core_returns) —
   *  stamps "RETURNED" over the page and overrides the Payment Status. */
  refund?: InvoiceRefund | null;
  /** core_orders.status === 'cancelled' — stamps "CANCELLED" instead. */
  cancelled?: boolean;
  cancelReason?: string | null;
}) {
  const t = (key: string, vars?: Record<string, string | number>) => invoiceText(template.text_overrides, key, vars);
  const brandName = template.issuer_name?.trim() || 'The.K Seoul';
  const concernLine = template.concern_line?.trim();
  const docTypeKey = DOC_TYPE_TEXT_KEY[request.doc_type];
  const docLabel = docTypeKey ? t(docTypeKey) : 'Document';
  const currency = request.currency || 'BDT';
  const concernSentence = concernLine ? `${brandName} is ${concernLine}` : null;
  const legalBits = [
    template.issuer_business_number ? `BIN: ${template.issuer_business_number}` : null,
    template.trade_license_no ? `Trade License No: ${template.trade_license_no}` : null,
  ].filter(Boolean);

  const website = template.issuer_website?.trim();
  const socialUrl = template.social_url?.trim();
  const qrValue = socialUrl
    ? /^https?:\/\//i.test(socialUrl)
      ? socialUrl
      : `https://${socialUrl}`
    : website
      ? /^https?:\/\//i.test(website)
        ? website
        : `https://${website}`
      : template.issuer_email
        ? `mailto:${template.issuer_email.trim()}`
        : null;
  const hasSignature = !!template.signature_url || !!template.stamp_url;

  return (
    <div
      className="invoice-doc bg-white mx-auto relative"
      style={{
        width: '210mm',
        // minHeight (not height) on purpose: an actual physical print
        // pipeline's usable page height varies too much (a printer driver's
        // own margins, Letter paper instead of A4, OS-level scaling) to
        // safely target a fixed mm value as an explicit height — every
        // explicit height tried here (297mm, 278mm, 250mm) overflowed onto
        // a near-empty second page on at least one real setup. minHeight
        // only ever adds space, never truncates, so a short invoice's
        // closing block (grid row 2, "1fr" below) still gets pushed down
        // to sit near the bottom of this floor — same visual result as
        // pinning to the page — while a genuinely long invoice just grows
        // the container past 220mm instead of overflowing a fixed one.
        // (position:fixed was also tried, for the literal true page edge —
        // reverted, it tripped a real Chromium print bug that added a
        // phantom 2nd page. This grid approach alone never did.)
        minHeight: '220mm',
        display: 'grid',
        gridTemplateRows: 'auto 1fr',
        fontFamily: "'Inter', system-ui, sans-serif",
        boxSizing: 'border-box',
        color: INK,
        breakInside: 'avoid',
        pageBreakInside: 'avoid',
      }}
    >
      {/* Watermark — a brand's own uploaded logo if set (desaturated, faint),
          else a restrained monogram fallback. Either way ~3-6% opacity so it
          reads as "official security paper" rather than a decoration
          competing with content. */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none flex items-center justify-center invoice-doc-watermark-wrapper" aria-hidden="true">
        {template.watermark_url ? (
          <img
            src={template.watermark_url}
            alt=""
            crossOrigin="anonymous"
            className="invoice-doc-watermark"
            style={{
              width: '380px',
              maxHeight: '380px',
              objectFit: 'contain',
              opacity: 0.08,
              filter: 'grayscale(1)',
            }}
          />
        ) : (
          <div
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontWeight: 600,
              fontSize: '480px',
              lineHeight: 1,
              color: NAVY,
              opacity: 0.035,
            }}
          >
            K
          </div>
        )}
      </div>
      <RefundWatermark refund={refund} cancelled={cancelled} cancelReason={cancelReason} />

      {/* Grid row 1 (auto height): header through terms/notes — flows
          naturally, top-anchored. */}
      <div className="relative" style={{ boxSizing: 'border-box' }}>
        {/* Marks the brand/doc-number block so downloadInvoicePdf.ts can
            capture it once and repeat it at the top of every page a long
            invoice spills onto — without it, a continuation page would
            start mid-table with no way to tell which invoice it's even
            for if separated from page 1. Carries its own page padding
            (rather than inheriting it from a shared parent, like the rest
            of this content below does) so that captured image is
            self-contained — otherwise re-drawing it alone on page 2+
            would sit flush against the page edges, with no margin. */}
        <div className="invoice-doc-header" style={{ padding: '8mm 16mm 0', boxSizing: 'border-box' }}>
        {/* Header */}
        <div className="flex items-center justify-between gap-8">
          <div className="flex items-center gap-3.5 min-w-0">
            {template.logo_url ? (
              // Logo already carries the brand name/wordmark — showing the
              // text name alongside it would just duplicate it.
              <img
                src={template.logo_url}
                alt={brandName}
                crossOrigin="anonymous"
                className="flex-shrink-0 object-contain"
                style={{ height: '76px', maxWidth: '260px' }}
              />
            ) : (
              <h1
                className="m-0"
                style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 600, fontSize: '30px', letterSpacing: '-0.015em', lineHeight: 1.05, color: NAVY }}
              >
                {brandName}
              </h1>
            )}
          </div>
          <div
            className="text-right flex-shrink-0 doc-badge"
            style={{
              background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_DEEP} 100%)`,
              borderRadius: '8px',
              padding: '8px 14px',
              minWidth: '140px',
            }}
          >
            <p className="m-0 text-[9.5px] font-bold uppercase" style={{ letterSpacing: '0.18em', color: GOLD }}>
              {docLabel}
            </p>
            <p
              className="mt-1 mb-0 text-white doc-badge-value"
              style={{ fontFamily: "'Inter', system-ui, sans-serif", fontWeight: 700, fontSize: '14px', letterSpacing: '0.01em', fontVariantNumeric: 'tabular-nums' }}
            >
              {request.doc_number || 'DRAFT'}
            </p>
          </div>
        </div>

        {/* Double rule: hairline gold over heavier navy */}
        <div style={{ borderTop: `1px solid ${GOLD}`, opacity: 1 }} />
        <div style={{ borderTop: `2px solid ${NAVY}`, marginTop: '3px' }} />
        </div>

        {/* Everything below shares the same 16mm side padding the header
            above carries on its own — kept in one wrapper since none of
            it needs to be captured/repeated separately the way the
            header does. */}
        <div style={{ padding: '0 16mm 0', boxSizing: 'border-box' }}>

        {/* Issuer / Bill-to / Dates — page 1 only, not repeated on
            continuation pages (that's just the brand + invoice number
            above; a customer's own details don't need restating). */}
        <div className="grid mt-4" style={{ gridTemplateColumns: '0.95fr 1.15fr 0.75fr', gap: '22px' }}>
          <Field label={t('label_from')}>
            <p className="font-semibold m-0">{brandName}</p>
            {(template.office_address || template.issuer_address) && (
              <p className="m-0 mt-0.5" style={{ color: MUTED }}>
                {template.office_address || template.issuer_address}
              </p>
            )}
            {template.issuer_contact && (
              <p className="m-0 mt-0.5" style={{ color: MUTED }}>
                {template.issuer_contact}
              </p>
            )}
          </Field>

          <Field label={t('label_bill_to')}>
            <p className="font-semibold m-0">{request.company_name || request.recipient_name || 'Customer'}</p>
            {request.business_number && (
              <p className="m-0 mt-0.5" style={{ color: MUTED }}>
                BIN: {request.business_number}
              </p>
            )}
            {request.company_address && (
              <p className="m-0 mt-0.5" style={{ color: MUTED }}>
                {request.company_address}
              </p>
            )}
            {request.company_contact && (
              <p className="m-0 mt-0.5" style={{ color: MUTED }}>
                {request.company_contact}
              </p>
            )}
            {request.recipient_email && (
              <p className="m-0 mt-0.5" style={{ color: '#3D5A8A' }}>
                {request.recipient_email}
              </p>
            )}
          </Field>

          <div className="space-y-1.5">
            <Field label={t('label_issue_date')}>{formatDate(request.issue_date)}</Field>
            {cancelled ? (
              <Field label={t('label_payment_status')}>
                <span className="font-semibold" style={{ color: '#B42318' }}>
                  Cancelled
                </span>
              </Field>
            ) : refund ? (
              <Field label={t('label_payment_status')}>
                <span className="font-semibold" style={{ color: '#B42318' }}>
                  {refundStatusLabel(refund)}
                  {refund.refundedAt ? ` · ${formatDate(refund.refundedAt)}` : ''}
                </span>
              </Field>
            ) : (
              payment && (
                <Field label={t('label_payment_status')}>
                  <span className="font-semibold" style={{ color: paymentStatusColor(payment) }}>
                    {paymentStatusLabel(payment)}
                  </span>
                </Field>
              )
            )}
            {salesPerson && <Field label={t('label_sales_person')}>{salesPerson}</Field>}
            {soldBy && <Field label={t('label_sold_by')}>{soldBy}</Field>}
          </div>
        </div>

        {/* Line items */}
        <table className="w-full mt-6" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: SOFT_BG }}>
              <th className="text-left py-2.5 pl-3 text-[9px] font-bold uppercase rounded-l-md" style={{ letterSpacing: '0.1em', color: MUTED }}>
                {t('th_description')}
              </th>
              <th className="text-center py-2.5 text-[9px] font-bold uppercase" style={{ width: '60px', letterSpacing: '0.1em', color: MUTED }}>
                {t('th_qty')}
              </th>
              <th className="text-right py-2.5 text-[9px] font-bold uppercase" style={{ width: '100px', letterSpacing: '0.1em', color: MUTED }}>
                {t('th_unit_price')}
              </th>
              <th className="text-right py-2.5 pr-3 text-[9px] font-bold uppercase rounded-r-md" style={{ width: '110px', letterSpacing: '0.1em', color: MUTED }}>
                {t('th_amount')}
              </th>
            </tr>
          </thead>
          <tbody>
            {request.line_items.length === 0 ? (
              <tr>
                <td colSpan={4} className="py-6 text-center text-[11px]" style={{ color: FAINT }}>
                  {t('empty_line_items')}
                </td>
              </tr>
            ) : (
              request.line_items.map((item, idx) => (
                <tr key={idx} style={{ borderBottom: `1px solid ${HAIRLINE}` }}>
                  <td className="py-2.5 pl-3 text-[11.5px]">
                    {item.description}
                    {item.serials && item.serials.length > 0 && (
                      <p className="m-0 mt-0.5 text-[8.5px] font-mono" style={{ color: FAINT, letterSpacing: '0.01em' }}>
                        {t('label_serial')} {item.serials.join(', ')}
                      </p>
                    )}
                  </td>
                  <td className="py-2.5 text-[11.5px] text-center" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {item.quantity}
                  </td>
                  <td className="py-2.5 text-[11.5px] text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatMoney(item.unit_price, currency)}
                  </td>
                  <td className="py-2.5 pr-3 text-[11.5px] text-right font-semibold" style={{ fontVariantNumeric: 'tabular-nums', color: NAVY }}>
                    {formatMoney(item.amount, currency)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Totals — invoice-doc-keep-together marks its top edge as a
            valid page-break point in downloadInvoicePdf.ts, the same way
            each table row's top edge already is, so a page break can
            never land inside this box and cut it in half. */}
        <div className="flex justify-end mt-5 invoice-doc-keep-together">
          <div style={{ width: '250px' }}>
            <div className="flex justify-between py-1.5" style={{ borderBottom: `1px solid ${HAIRLINE}` }}>
              <span className="text-[11px]" style={{ color: MUTED }}>
                {t('label_subtotal')}
              </span>
              <span className="text-[11.5px] font-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatMoney(request.subtotal, currency)}
              </span>
            </div>
            {request.shipping_bdt > 0 && (
              <div className="flex justify-between py-1.5" style={{ borderBottom: `1px solid ${HAIRLINE}` }}>
                <span className="text-[11px]" style={{ color: MUTED }}>
                  {t('label_delivery_charge')}
                </span>
                <span className="text-[11.5px] font-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatMoney(request.shipping_bdt, currency)}
                </span>
              </div>
            )}
            {request.discount_bdt > 0 && (
              <div className="flex justify-between py-1.5" style={{ borderBottom: `1px solid ${HAIRLINE}` }}>
                <span className="text-[11px]" style={{ color: '#B7461F' }}>
                  {t('label_discount')}
                </span>
                <span className="text-[11.5px] font-semibold" style={{ fontVariantNumeric: 'tabular-nums', color: '#B7461F' }}>
                  -{formatMoney(request.discount_bdt, currency)}
                </span>
              </div>
            )}
            {/* Memo only, not part of the sum above — VAT is already
                price-inclusive in Subtotal, so this never adds to Total.
                Same label-left/amount-right row shape as Subtotal/Discount
                above, just smaller and muted so it still reads as a note. */}
            {request.tax_rate > 0 && (
              <div className="flex justify-between py-1 mt-0.5">
                <span className="text-[9.5px]" style={{ color: FAINT }}>
                  {template.show_vat_amount === false
                    ? t('vat_note_rate_only', { rate: request.tax_rate })
                    : t('vat_note_with_amount', { rate: request.tax_rate })}
                </span>
                {template.show_vat_amount !== false && (
                  <span className="text-[9.5px]" style={{ color: FAINT, fontVariantNumeric: 'tabular-nums' }}>
                    {formatMoney(request.tax_amount, currency)}
                  </span>
                )}
              </div>
            )}
            <div
              className="flex justify-between items-center mt-2 doc-badge"
              style={{
                background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_DEEP} 100%)`,
                borderRadius: '8px',
                padding: '9px 14px',
              }}
            >
              <span className="text-[9px] font-bold uppercase" style={{ letterSpacing: '0.14em', color: GOLD }}>
                {t('label_total')}
              </span>
              <span className="text-white doc-badge-value" style={{ fontFamily: "'Inter', system-ui, sans-serif", fontWeight: 800, fontSize: '20px', letterSpacing: '0.01em', fontVariantNumeric: 'tabular-nums' }}>
                {formatMoney(request.total_amount, currency)}
              </span>
            </div>
          </div>
        </div>

        {/* Terms / payment method / notes — payment method is the "how"
            (Cash on Delivery, bKash, card, etc.); the Paid/Unpaid "status"
            itself lives up in the Issue Date column, so this box never
            repeats that word. invoice-doc-keep-together (see Totals
            above) — a page break must never land inside this row and
            cut one of its boxes (e.g. Payment Method) in half. */}
        {(request.payment_terms || payment || request.notes) && (
          <div
            className="mt-6 grid gap-3 invoice-doc-keep-together"
            style={{ gridTemplateColumns: `repeat(${[request.payment_terms, payment, request.notes].filter(Boolean).length}, 1fr)` }}
          >
            {request.payment_terms && (
              <div className="rounded-md p-3" style={{ background: SOFT_BG, borderLeft: `3px solid ${GOLD}` }}>
                <p className="m-0 text-[8.5px] font-bold uppercase" style={{ letterSpacing: '0.12em', color: '#8A6A34' }}>
                  {t('label_payment_terms')}
                </p>
                <p className="m-0 mt-1 text-[11px]" style={{ color: '#374151' }}>
                  {request.payment_terms}
                </p>
              </div>
            )}
            {payment && (
              <div className="rounded-md p-3" style={{ background: SOFT_BG, borderLeft: `3px solid ${GOLD}` }}>
                <p className="m-0 text-[8.5px] font-bold uppercase" style={{ letterSpacing: '0.12em', color: '#8A6A34' }}>
                  {t('label_payment_method')}
                </p>
                <p className="m-0 mt-1 text-[11px]" style={{ color: '#374151' }}>
                  {payment.methodLabel ?? formatPaymentMethodLabel(payment.method)}
                  {payment.paid && payment.amountBdt != null
                    ? ` · ${formatMoney(payment.amountBdt, currency)}${payment.paidAt ? ` · ${formatDate(payment.paidAt)}` : ''}`
                    : ''}
                </p>
              </div>
            )}
            {request.notes && (
              <div className="rounded-md p-3" style={{ background: SOFT_BG }}>
                <p className="m-0 text-[8.5px] font-bold uppercase" style={{ letterSpacing: '0.12em', color: FAINT }}>
                  {t('label_notes')}
                </p>
                <p className="m-0 mt-1 text-[11px]" style={{ color: '#374151' }}>
                  {request.notes}
                </p>
              </div>
            )}
          </div>
        )}
        </div>
      </div>

      {/* Grid row 2 ("1fr" above): a bottom-aligned flex column, so the
          closing block sits at the bottom of the container's minHeight
          floor on a short invoice — with whatever gap is left automatically
          above it — instead of just following the content directly. Safe
          against overflow because it's minHeight driving this, not a fixed
          height: a long invoice just grows the 1fr row (and the container)
          instead of the closing block ever being pushed past a hard ceiling.
          The invoice-doc-footer marker also lets downloadInvoicePdf.ts
          capture this block separately for the Download PDF pipeline. */}
      <div className="relative flex flex-col justify-end invoice-doc-footer" style={{ padding: '0 16mm 8mm', boxSizing: 'border-box' }}>
        {/* Closing section — a clean, centered sign-off instead of a split
            QR/signature row with a separate "Scan to Connect" label. When a
            real signature/stamp is on file it still gets its own dedicated
            row (that's a document being formally signed); otherwise it's
            just a short thank-you + notice + QR, centered, one after the
            other. break-inside:avoid so a print engine never cuts through
            the middle of this block. */}
        <div style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
          {hasSignature && (
            <div className="flex items-end justify-between" style={{ minHeight: '70px' }}>
              {qrValue ? (
                <div className="rounded-lg" style={{ padding: '7px', border: `1px solid ${GOLD}` }}>
                  <QRCodeSVG value={qrValue} size={56} fgColor={NAVY} bgColor="transparent" level="M" />
                </div>
              ) : (
                <div />
              )}
              <div className="text-center" style={{ minWidth: '200px' }}>
                <div className="flex items-center justify-center gap-3" style={{ height: '58px' }}>
                  {template.signature_url && (
                    <img src={template.signature_url} alt="Signature" crossOrigin="anonymous" style={{ height: '52px', objectFit: 'contain' }} />
                  )}
                  {template.stamp_url && (
                    <img src={template.stamp_url} alt="Stamp" crossOrigin="anonymous" style={{ height: '52px', objectFit: 'contain' }} />
                  )}
                </div>
                <div style={{ borderTop: `1px solid ${HAIRLINE}`, paddingTop: '6px' }}>
                  <p className="m-0 text-[10.5px] font-semibold" style={{ color: NAVY }}>
                    {brandName}
                  </p>
                  <p className="m-0 text-[9px]" style={{ color: FAINT }}>
                    {t('label_authorized_signature')}
                  </p>
                </div>
              </div>
            </div>
          )}
          {!hasSignature && (
            <div className="flex items-center justify-between" style={{ minHeight: '70px' }}>
              {qrValue ? (
                <div className="flex items-center gap-3">
                  <div className="rounded-lg" style={{ padding: '7px', border: `1px solid ${GOLD}` }}>
                    <QRCodeSVG value={qrValue} size={56} fgColor={NAVY} bgColor="transparent" level="M" />
                  </div>
                  <p className="m-0 text-[9px] font-bold uppercase" style={{ letterSpacing: '0.1em', color: '#8A6A34', whiteSpace: 'pre-line' }}>
                    {t('label_scan_to_connect')}
                  </p>
                </div>
              ) : (
                <div />
              )}
              <div className="text-right" style={{ maxWidth: '260px' }}>
                <p className="m-0 text-[11px] font-semibold" style={{ color: NAVY }}>
                  {t('thank_you_message', { brand: brandName })}
                </p>
                <p className="m-0 mt-1.5 text-[10px]" style={{ color: MUTED }}>
                  {t('no_signature_note')}
                </p>
              </div>
            </div>
          )}

          {/* Legal footer — tight to the QR/signature row above it, reading
              as one closing block instead of a separate floating element. */}
          <div
            className="mt-5 text-center"
            style={{
              background: `linear-gradient(180deg, ${SOFT_BG} 0%, #F0F2F7 100%)`,
              borderTop: `2px solid ${GOLD}`,
              borderRadius: '12px 12px 0 0',
              padding: '14px 26px 12px',
              marginLeft: '-16mm',
              marginRight: '-16mm',
              marginBottom: '-8mm',
            }}
          >
            {concernSentence && (
              <p className="m-0 text-[10.5px] font-semibold" style={{ color: NAVY, letterSpacing: '0.01em' }}>
                {concernSentence}
              </p>
            )}
            <div className="mx-auto" style={{ width: '30px', height: '1.5px', background: GOLD, marginTop: '8px', marginBottom: '9px' }} />
            <div className="flex items-center justify-center flex-wrap" style={{ columnGap: '18px', rowGap: '5px' }}>
              {legalBits.length > 0 && (
                <span className="inline-flex items-center gap-1" style={{ color: FAINT }}>
                  <Landmark className="w-3 h-3 flex-shrink-0" style={{ color: GOLD }} />
                  <span className="text-[8.5px]" style={{ letterSpacing: '0.02em' }}>
                    {legalBits.join('  ·  ')}
                  </span>
                </span>
              )}
              {template.issuer_email && (
                <span className="inline-flex items-center gap-1" style={{ color: '#3D5A8A' }}>
                  <Mail className="w-3 h-3 flex-shrink-0" style={{ color: GOLD }} />
                  <span className="text-[8.5px] font-medium">{template.issuer_email}</span>
                </span>
              )}
              {template.issuer_website && (
                <span className="inline-flex items-center gap-1" style={{ color: '#3D5A8A' }}>
                  <Globe className="w-3 h-3 flex-shrink-0" style={{ color: GOLD }} />
                  <span className="text-[8.5px] font-medium">{template.issuer_website}</span>
                </span>
              )}
              {template.issuer_address && (
                <span className="inline-flex items-center gap-1" style={{ color: '#B4BAC6' }}>
                  <MapPin className="w-3 h-3 flex-shrink-0" style={{ color: GOLD }} />
                  <span className="text-[8.5px]">{template.issuer_address}</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
